/**
 * Protected route component
 * Restricts access to authenticated users only
 * 
 * Authentication is enabled - all routes using this component require authentication
 */
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@store/auth.store';

/**
 * Protected route props
 */
interface ProtectedRouteProps {
    requiredRoles?: string[];
    redirectPath?: string;
}

// Enable authentication - set to false to disable
const DISABLE_AUTH = false;

/**
 * Protected route component
 * @param props - Protected route properties
 * @returns JSX Element
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    requiredRoles = [],
    redirectPath = '/login',
}) => {
    const { isAuthenticated, user } = useAuthStore();

    // If auth is disabled (for development/testing), allow access
    if (DISABLE_AUTH) {
        return <Outlet />;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
        return <Navigate to={redirectPath} replace />;
    }

    // Check if user has required roles
    if (requiredRoles.length > 0 && user) {
        const hasRequiredRole = requiredRoles.some((role) => user.roles.includes(role));
        if (!hasRequiredRole) {
            return <Navigate to="/unauthorized" replace />;
        }
    }

    return <Outlet />;
};

export default ProtectedRoute;
